import type { BenefitInfoContent } from '../new-elite-landing/components/NewEliteLandingPageBenefits'
import {
  benefitInfoContentMap,
  eliteBenefitInfoTabTitles
} from '../new-elite-landing/components/NewEliteLandingPageBenefits'
import type { ElitePlansBenefitSheetVariant } from './types/features.types'

export type { ElitePlansBenefitSheetVariant } from './types/features.types'

const eliteFeatureBenefitTabMap: Record<string, string> = {
  'power-rank': 'Power Rank',
  'rebalance-plan-feat': 'Power Rebalance',
  'power-select': 'Power Select',
  'tax-summary': 'Tax Saving Plan',
  'global-funds': 'Global Funds',
  'regular-to-direct': 'Regular to Direct plan',
  'elite-less': 'Elite Lens'
}

// Elite-plans-only tab; the new-elite landing sheet keeps its original tab strip
const eliteGlobalFundsBenefitInfo: BenefitInfoContent = {
  rive: '/rives/elite-plans/global.riv',
  sectionTitle: 'FUND SELECTION & REVIEW',
  title:
    '<span class="font-bold text-black">Global Funds</span> <span class="text-grey40 font-normal">for diversification beyond India</span>',
  titleAriaLabel: 'Global Funds for diversification beyond India',
  description:
    'Discover and invest in global funds which are open for investments and expand your portfolio beyond the Indian market.'
}

const elitePrimeFeatureBenefitTabMap: Record<string, string> = {
  'strategy-planning': 'Allocation Strategy',
  'allocation-plan-feat': 'Allocation Plan',
  'sip-plan-feat': 'SIP Plan',
  'gold-silver-global': 'Gold, Silver & Global Advisory',
  'lumpsum-investment-strategy': 'Investment Strategy',
  'withdrawal-strategy': 'Withdrawal Strategy',
  'advisor-support-feat': 'Access to Investment Coach'
}

export const elitePrimeBenefitInfoTabTitles = [
  'Allocation Strategy',
  'Allocation Plan',
  'SIP Plan',
  'Gold, Silver & Global Advisory',
  'Investment Strategy',
  'Withdrawal Strategy',
  'Access to Investment Coach'
]

const ELITE_PRIME_FEATURE_EYEBROW = 'Elite Prime Exclusive'

export const elitePrimeBenefitInfoContentMap: Record<string, BenefitInfoContent> = {
  'Allocation Strategy': {
    rive: '/rives/elite-plans/strategy.riv',
    sectionTitle: 'WEALTH MANAGEMENT',
    eyebrow: ELITE_PRIME_FEATURE_EYEBROW,
    title:
      '<span class="font-bold text-black">Allocation Strategy</span> <span class="text-grey40 font-normal">for identifying how you should invest</span>',
    titleAriaLabel: 'Allocation Strategy for identifying how you should invest',
    description:
      'Find out what kind of investor you are based on your financial goals, life stage and risk appetite. Our experts review these factors to create your personalised allocation strategy that guides how your portfolio should be invested.'
  },
  'Allocation Plan': {
    rive: '/rives/elite-plans/allocation.riv',
    sectionTitle: 'WEALTH MANAGEMENT',
    eyebrow: ELITE_PRIME_FEATURE_EYEBROW,
    title:
      '<span class="font-bold text-black">Allocation Plan</span> <span class="text-grey40 font-normal">for aligning your portfolio with your financial goals</span>',
    titleAriaLabel: 'Allocation Plan for aligning your portfolio with your financial goals',
    description:
      'Our experts review how your portfolio is allocated and create a step-by-step plan to align it with your personalised allocation strategy. The plan optimises your overall portfolio allocation in a tax-efficient manner along with performance, diversification and fund overlaps.'
  },
  'SIP Plan': {
    rive: '/rives/elite-plans/SIP.riv',
    sectionTitle: 'WEALTH MANAGEMENT',
    eyebrow: ELITE_PRIME_FEATURE_EYEBROW,
    title:
      '<span class="font-bold text-black">SIP Plan</span> <span class="text-grey40 font-normal">for disciplined investing in the right funds</span>',
    titleAriaLabel: 'SIP Plan for disciplined investing in the right funds',
    description:
      "Get a plan that guides you on exactly which SIPs to start, increase, decrease or stop, so your portfolio's allocation stays on the right path over time."
  },
  'Gold, Silver & Global Advisory': {
    rive: '/rives/elite-plans/gold&global.riv',
    sectionTitle: 'ADVISORY',
    eyebrow: ELITE_PRIME_FEATURE_EYEBROW,
    title:
      '<span class="font-bold text-black">Gold, Silver & Global Advisory</span> <span class="text-grey40 font-normal">for diversification beyond Indian equities</span>',
    titleAriaLabel: 'Gold, Silver and Global Advisory for diversification beyond Indian equities',
    description:
      'Diversify across gold, silver and global funds with personalised expert guidance. Know exactly how much you should be holding in each category based on your allocation strategy.'
  },
  'Investment Strategy': {
    rive: '/rives/elite-plans/new_lumpsum.riv',
    sectionTitle: 'ADVISORY',
    eyebrow: ELITE_PRIME_FEATURE_EYEBROW,
    title:
      '<span class="font-bold text-black">Lump Sum Investment Plan</span> <span class="text-grey40 font-normal">for investing new lump sum amounts intelligently</span>',
    titleAriaLabel: 'Lump Sum Investment Plan for investing new lump sum amounts intelligently',
    description:
      'Tell us how much you want to invest. Get a clear action plan that gives you the right set of funds you should invest in, based on your allocation strategy.',
    comingSoon: false
  },
  'Withdrawal Strategy': {
    rive: '/rives/elite-plans/withdraw.riv',
    sectionTitle: 'ADVISORY',
    eyebrow: ELITE_PRIME_FEATURE_EYEBROW,
    title:
      '<span class="font-bold text-black">Withdrawal Strategy</span> <span class="text-grey40 font-normal">for smarter, tax-aware withdrawals</span>',
    titleAriaLabel: 'Withdrawal Strategy for smarter, tax-aware withdrawals',
    description:
      'Tell us how much money you need to withdraw. Get a clear action plan on what to sell and in what order, optimised for taxes, exit load and your allocation strategy.',
    comingSoon: true
  },
  'Access to Investment Coach': {
    rive: '/rives/elite-plans/24_7_advisor.riv',
    sectionTitle: 'ADVISORY',
    eyebrow: ELITE_PRIME_FEATURE_EYEBROW,
    title:
      '<span class="font-bold text-black">Access to Investment Coach</span> <span class="text-grey40 font-normal">for expert guidance whenever you need it</span>',
    titleAriaLabel: 'Access to Investment Coach for expert guidance whenever you need it',
    description: 'Have a question about your portfolio or allocation strategy? Email us anytime and get answers.'
  }
}

export function resolveFeatureBenefitTabTitle(
  featureId: string,
  variant: ElitePlansBenefitSheetVariant
): string | undefined {
  const tabMap = variant === 'elitePrime' ? elitePrimeFeatureBenefitTabMap : eliteFeatureBenefitTabMap
  return tabMap[featureId]
}

/** Members see "Unlocked"; Prime includes Elite, so a Prime user also has the Elite sheet unlocked. */
export function getElitePlansBenefitSheetConfig(
  variant: ElitePlansBenefitSheetVariant,
  { isUserElite, isUserElitePrime }: { isUserElite: boolean; isUserElitePrime: boolean }
): {
  sheetTitle: string
  tabTitles: string[]
  contentMap: Record<string, BenefitInfoContent>
} {
  if (variant === 'elitePrime') {
    return {
      sheetTitle: isUserElitePrime ? 'Unlocked with Elite Prime' : 'Unlock with Elite Prime',
      tabTitles: elitePrimeBenefitInfoTabTitles,
      contentMap: elitePrimeBenefitInfoContentMap
    }
  }

  return {
    sheetTitle: isUserElite || isUserElitePrime ? 'Unlocked with Elite' : 'Unlock with Elite',
    tabTitles: [...eliteBenefitInfoTabTitles, 'Global Funds'],
    contentMap: { ...benefitInfoContentMap, 'Global Funds': eliteGlobalFundsBenefitInfo }
  }
}
